<template>
  <div class="flex justify-center">
    <Form ref="form" :validation-schema="schema" class="w-full" @submit="onSubmit">
      <Message v-if="error" severity="error" :closable="false">
        {{ error.message }}
      </Message>

      <SocialMediaLoginFormV2 v-if="showNewDesign" :label="$t('oauth.google.register')" />
      <SocialMediaLoginForm v-else-if="showOldDesign" :label="$t('oauth.google.register')" />

      <div class="space-y-4 mb-4 mt-8">
        <BasicRadio name="userType" :items="userTypes" />
        <BasicInputText name="firstName" :label="$t('first_name')" />
        <BasicInputText name="lastName" :label="$t('last_name')" />
        <BasicInputText type="email" name="email" :label="$t('email')" />
        <BasicInputText name="password" type="password" :label="$t('password')" />
        <BasicInputText name="passwordConfirm" type="password" :label="$t('password_confirm')" />

        <BasicCheckbox name="accept" :label="$t('i_am_interested_in_receiving')" binary />
      </div>

      <div v-if="loading" class="text-center">
        <Progressspinner class="w-8 h-8 mb-3" />
      </div>

      <div class="mb-4 mt-4 flex justify-center">
        <vue-friendly-captcha
          :key="captchaKey"
          :sitekey="FRIENDLY_CAPTCHA_SITEKEY"
          :puzzleEndpoint="FRIENDLY_CAPTCHA_PUZZLE_ENDPOINT"
          @done="saveRecaptchaToken"
        />
      </div>

      <Button
        :disabled="loading"
        type="submit"
        severity="primary"
        :label="$t('register_now')"
        icon="pi pi-user"
        class="w-full"
        rounded
      ></Button>
    </Form>
  </div>
</template>

<script setup lang="ts">
import * as yup from 'yup'
import { storeToRefs } from 'pinia'
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRegisterCustomerStore } from '@/stores/register-customer'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { Form } from 'vee-validate'

import Button from 'primevue/button'
import Progressspinner from 'primevue/progressspinner'
import Message from 'primevue/message'

import BasicInputText from '@/components/forms/inputs/BasicInputText.vue'
import BasicRadio from '@/components/forms/inputs/BasicRadio.vue'
import BasicCheckbox from '@/components/forms/inputs/BasicCheckbox.vue'
import SocialMediaLoginForm from '@/components/forms/SocialMediaLoginForm.vue'
import SocialMediaLoginFormV2 from '@/components/forms/SocialMediaLoginFormV2.vue'

import UserTypes from '@/enums/UserTypes'

import VueFriendlyCaptcha from '@somushq/vue3-friendly-captcha'

import {
  CAPTCHA_DISABLED,
  FRIENDLY_CAPTCHA_PUZZLE_ENDPOINT,
  FRIENDLY_CAPTCHA_SITEKEY
} from '@/entrypoint'

const { t, locale } = useI18n()
const { registerCustomer, reset } = useRegisterCustomerStore()
const { error, loading, success } = storeToRefs(useRegisterCustomerStore())
const toast = useToast()
const router = useRouter()

const form = ref()
const captchaKey = ref('recaptcha')
const recaptchaToken = ref()

const userTypes = ref([
  { label: t('business_customer'), value: UserTypes.TYPE_CUSTOMER_BUSINESS },
  { label: t('private_customer'), value: UserTypes.TYPE_CUSTOMER_PRIVATE }
])

const schema = yup.object({
  firstName: yup.string().required(t('validationErrorMessages.first_name_required')),
  lastName: yup.string().required(t('validationErrorMessages.last_name_required')),
  email: yup
    .string()
    .required(t('validationErrorMessages.email_required'))
    .email(t('validationErrorMessages.invalid_email')),
  password: yup
    .string()
    .required(t('validationErrorMessages.password_required'))
    .min(8, t('validationErrorMessages.password_length_error')),
  passwordConfirm: yup
    .string()
    .required(t('validationErrorMessages.password_doesnot_match'))
    .min(8, t('validationErrorMessages.password_doesnot_match'))
    .oneOf([yup.ref('password')], t('validationErrorMessages.password_doesnot_match')),
  accept: yup.boolean().optional(),
  userType: yup
    .string()
    .required(t('validationErrorMessages.user_type_required'))
    .oneOf(Object.values(UserTypes))
})

const showNewDesign = ref(false)
const showOldDesign = ref(false)

onMounted(() => {
  updateFeatureFlags()
  window.posthog.onFeatureFlags(updateFeatureFlags)
})

const updateFeatureFlags = () => {
  const flagValue = window.posthog.getFeatureFlag('social-auth-login')
  showNewDesign.value = flagValue === 'new_design'
  showOldDesign.value = flagValue === 'old_design'
}

const saveRecaptchaToken = (token: string) => {
  recaptchaToken.value = token
}

const onSubmit = async (data: any) => {
  if (!recaptchaToken.value && !CAPTCHA_DISABLED) {
    toast.add({
      severity: 'error',
      summary: t('recaptcha'),
      detail: t('invalid_captcha'),
      life: 5000
    })

    return
  }

  const userData = {
    userType: data.userType,
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    password: data.password,
    optOut: data.optOut,
    language: locale.value,
    recaptchaToken: recaptchaToken.value
  }

  registerCustomer(userData)
}

const resetRecaptcha = () => {
  captchaKey.value = 'recaptcha' + Date.now()
  recaptchaToken.value = null
}

watch(error, () => {
  if (!error.value || !error.value.violations) {
    return
  }

  error.value.violations.map((violation: any) => {
    form.value.setFieldError(violation.propertyPath, violation.message)
  })

  resetRecaptcha()
})

watch(success, () => {
  if (!success.value) return;

  const { email, firstName, lastName, userType } = form.value?.values || {};
  const identifyPosthogUser = (window as any).identifyPosthogUser;

  if (email && window.posthog && identifyPosthogUser) {
    identifyPosthogUser(email, firstName, lastName, userType);
  }

  reset();

  toast.add({
    severity: 'success',
    summary: t('thank_you'),
    detail: t('data_successfully_saved'),
    life: 5000
  });

  router.push('/login');
});

</script>
